import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CircleNotch, CheckCircle, Scales, UserGear, User, CaretRight, CaretLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * New User wizard — the Operations Console create flow.
 * Barrister / Clerk → basics → tenant → role → relationships → review.
 * Creates the account and sends the invitation in one server action.
 */

type UserKind = 'barrister' | 'clerk' | 'solo';

interface TenantOption { id: string; name: string }
interface RelUser { id: string; firstName: string; lastName: string; email: string; memberType: string }

const BARRISTER_ROLES = [
  { value: 'silk', label: "KC (Silk)" },
  { value: 'junior', label: 'Junior Barrister' },
  { value: 'pupil', label: 'Pupil' },
];
const CLERK_ROLES = [
  { value: 'senior_clerk', label: 'Senior Clerk' },
  { value: 'junior_clerk', label: 'Junior Clerk' },
];

const input =
  'h-9 w-full rounded-lg border border-plat-border bg-plat-sunken px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-1 focus:ring-plat-focus/50';
const label = 'mb-1.5 block text-xs font-medium text-plat-secondary';

function normalizeList<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  const r = raw as Record<string, unknown> | null;
  if (r && Array.isArray(r.data)) return r.data as T[];
  if (r && Array.isArray(r.tenants)) return r.tenants as T[];
  return [];
}

export function NewUserWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState<UserKind | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [chambersId, setChambersId] = useState('');
  const [memberType, setMemberType] = useState('');
  const [relationshipUserIds, setRelationshipUserIds] = useState<string[]>([]);

  const reset = () => {
    setStep(1); setKind(null); setFirstName(''); setLastName(''); setEmail('');
    setPhone(''); setJobTitle(''); setChambersId(''); setMemberType(''); setRelationshipUserIds([]);
  };
  const close = () => { reset(); onClose(); };

  const tenantsQ = useQuery({
    queryKey: ['platform', 'tenants', 'all'],
    // limit 100 = the endpoint's max (listTenantsSchema caps at 100). Requesting 500 failed zod
    // validation → 400 → the dropdown silently came back empty even when workspaces existed.
    queryFn: async () => {
      const res = await platformApi.get('/tenants', { params: { limit: 100 } });
      return normalizeList<TenantOption>(res.data?.data ?? res.data);
    },
    enabled: open,
  });

  // Relationship candidates in the chosen tenant, opposite side to the new user.
  const relKind = kind === 'barrister' ? 'clerk' : 'barrister';
  const relQ = useQuery({
    queryKey: ['platform', 'users', 'rel', chambersId, relKind],
    queryFn: async () => {
      const res = await platformApi.get('/users', { params: { chambersId, limit: 100 } });
      const list = normalizeList<RelUser>(res.data?.data ?? res.data);
      const barristerTypes = ['silk', 'junior', 'pupil'];
      const clerkTypes = ['senior_clerk', 'junior_clerk'];
      const want = relKind === 'clerk' ? clerkTypes : barristerTypes;
      return list.filter((u) => want.includes(u.memberType));
    },
    enabled: open && step === 5 && !!chambersId,
  });

  const isSolo = kind === 'solo';

  const createMutation = useMutation({
    mutationFn: async () => {
      // Solo barrister = a NEW one-person workspace — no chambers/role/relationships. The server
      // provisions the workspace + silk barrister + activation email in one action.
      if (isSolo) {
        const res = await platformApi.post('/tenants/solo', {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
        });
        return (res.data?.data ?? res.data) as { inviteEmailStatus?: string };
      }
      const res = await platformApi.post('/users', {
        memberType,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        ...(phone.trim() && { phone: phone.trim() }),
        ...(jobTitle.trim() && { jobTitle: jobTitle.trim() }),
        chambersId,
        ...(relationshipUserIds.length > 0 && { relationshipUserIds }),
      });
      return (res.data?.data ?? res.data) as { inviteEmailStatus: string };
    },
    onSuccess: (r) => {
      if (r.inviteEmailStatus === 'failed') {
        toast.warning('Created, but the activation email failed to send. You can resend it from their profile.');
      } else {
        toast.success(isSolo ? 'Solo barrister created — activation email sent' : 'User created and invitation sent');
      }
      queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      close();
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err, isSolo ? 'Failed to create solo barrister' : 'Failed to create user')),
  });

  const tenants = tenantsQ.data ?? [];
  const roles = kind === 'barrister' ? BARRISTER_ROLES : CLERK_ROLES;

  const canNext = useMemo(() => {
    if (step === 1) return kind !== null;
    if (step === 2) return firstName.trim() && lastName.trim() && /.+@.+\..+/.test(email);
    if (step === 3) return !!chambersId;
    if (step === 4) return !!memberType;
    return true;
  }, [step, kind, firstName, lastName, email, chambersId, memberType]);

  // Solo has NO workspace/role/relationships steps — jump basics(2) → review(6). The stepper shows
  // 3 steps for solo, 6 otherwise.
  const goNext = () => setStep((s) => (isSolo && s === 2 ? 6 : s + 1));
  const goBack = () => setStep((s) => (isSolo && s === 6 ? 2 : s - 1));
  const displayStep = isSolo ? (step === 6 ? 3 : step) : step;
  const totalSteps = isSolo ? 3 : 6;

  if (!open) return null;

  const tenantName = tenants.find((t) => t.id === chambersId)?.name ?? '—';
  const roleLabel = roles.find((r) => r.value === memberType)?.label ?? '—';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 p-4 sm:items-center">
      <ScrollLock />
      <div className="w-full max-w-lg rounded-2xl border border-plat-border bg-plat-elevated shadow-2xl">
        {/* Header + stepper */}
        <div className="flex items-center justify-between border-b border-plat-border px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-plat-fg">New user</h3>
            <p className="text-xs text-plat-muted">Step {displayStep} of {totalSteps}</p>
          </div>
          <button onClick={close} className="rounded p-1 text-plat-muted hover:bg-plat-hover hover:text-plat-secondary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-5">
          {/* Step 1 — kind */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-plat-secondary">What kind of user are you creating?</p>
              <div className="grid grid-cols-3 gap-3">
                {([['barrister', 'Barrister', Scales], ['solo', 'Solo Barrister', User], ['clerk', 'Clerk', UserGear]] as const).map(([k, lbl, Icon]) => (
                  <button
                    key={k}
                    onClick={() => { setKind(k); setMemberType(''); setRelationshipUserIds([]); }}
                    className={`flex flex-col items-center gap-2 rounded-xl border px-4 py-6 transition ${
                      kind === k ? 'border-plat-accent-solid bg-plat-info-soft' : 'border-plat-border bg-plat-surface hover:border-plat-neutral-line'
                    }`}
                  >
                    <Icon className={`h-7 w-7 ${kind === k ? 'text-plat-info-text' : 'text-plat-muted'}`} />
                    <span className={`text-sm font-medium ${kind === k ? 'text-plat-fg' : 'text-plat-secondary'}`}>{lbl}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — basics */}
          {step === 2 && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>First name</label><input className={input} value={firstName} onChange={(e) => setFirstName(e.target.value)} /></div>
              <div><label className={label}>Last name</label><input className={input} value={lastName} onChange={(e) => setLastName(e.target.value)} /></div>
              <div className="col-span-2"><label className={label}>Email</label><input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@chambers.co.uk" /></div>
              <div><label className={label}>Phone (optional)</label><input className={input} value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
              <div><label className={label}>Title (optional)</label><input className={input} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="e.g. Head of Chambers" /></div>
            </div>
          )}

          {/* Step 3 — tenant */}
          {step === 3 && (
            <div>
              <label className={label}>Assign to chambers / practice</label>
              {tenantsQ.isLoading ? (
                <div className="py-6 text-center"><CircleNotch className="mx-auto h-5 w-5 animate-spin text-plat-info-text" /></div>
              ) : (
                <select className={input} value={chambersId} onChange={(e) => { setChambersId(e.target.value); setRelationshipUserIds([]); }}>
                  <option value="">Select a workspace…</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Step 4 — role */}
          {step === 4 && (
            <div className="space-y-2">
              <label className={label}>Role</label>
              {roles.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setMemberType(r.value)}
                  className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-sm transition ${
                    memberType === r.value ? 'border-plat-accent-solid bg-plat-info-soft text-plat-fg' : 'border-plat-border bg-plat-surface text-plat-secondary hover:border-plat-neutral-line'
                  }`}
                >
                  {r.label}
                  {memberType === r.value && <CheckCircle className="h-4 w-4 text-plat-info-text" weight="fill" />}
                </button>
              ))}
            </div>
          )}

          {/* Step 5 — relationships */}
          {step === 5 && (
            <div>
              <label className={label}>
                {kind === 'barrister' ? 'Clerks who support this barrister (optional)' : 'Barristers this clerk supports (optional)'}
              </label>
              {relQ.isLoading ? (
                <div className="py-6 text-center"><CircleNotch className="mx-auto h-5 w-5 animate-spin text-plat-info-text" /></div>
              ) : (relQ.data ?? []).length === 0 ? (
                <p className="rounded-lg border border-plat-border bg-plat-surface px-3 py-4 text-center text-sm text-plat-muted">
                  No {relKind === 'clerk' ? 'clerks' : 'barristers'} in this workspace yet. You can link them later.
                </p>
              ) : (
                <div className="max-h-56 space-y-1.5 overflow-y-auto">
                  {(relQ.data ?? []).map((u) => {
                    const checked = relationshipUserIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        onClick={() => setRelationshipUserIds((ids) => checked ? ids.filter((i) => i !== u.id) : [...ids, u.id])}
                        className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                          checked ? 'border-plat-accent-solid bg-plat-info-soft' : 'border-plat-border bg-plat-surface hover:border-plat-neutral-line'
                        }`}
                      >
                        <span>
                          <span className="text-plat-fg">{u.firstName} {u.lastName}</span>
                          <span className="ml-2 text-xs text-plat-muted">{u.email}</span>
                        </span>
                        {checked && <CheckCircle className="h-4 w-4 text-plat-info-text" weight="fill" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 6 — review */}
          {step === 6 && (
            <div className="space-y-2 text-sm">
              <p className="mb-3 text-plat-secondary">
                {isSolo
                  ? 'Creates a new solo practice for this barrister and emails them an activation link to set their password.'
                  : 'Review and create. An invitation email will be sent so they can set their own password.'}
              </p>
              {(isSolo
                ? [
                    ['Type', 'Solo Barrister (KC)'],
                    ['Name', `${firstName} ${lastName}`.trim()],
                    ['Email', email],
                    ['Workspace', 'New solo practice (created automatically)'],
                    ['Plan', 'Single'],
                  ]
                : [
                    ['Type', kind === 'barrister' ? 'Barrister' : 'Clerk'],
                    ['Name', `${firstName} ${lastName}`.trim()],
                    ['Email', email],
                    ['Phone', phone || '—'],
                    ['Title', jobTitle || '—'],
                    ['Chambers', tenantName],
                    ['Role', roleLabel],
                    ['Relationships', relationshipUserIds.length ? `${relationshipUserIds.length} linked` : 'None'],
                  ]
              ).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-plat-border pb-1.5">
                  <span className="text-plat-muted">{k}</span>
                  <span className="text-plat-fg">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-plat-border px-5 py-4">
          <button
            onClick={() => (step === 1 ? close() : goBack())}
            className="inline-flex items-center gap-1 rounded-lg border border-plat-border px-3 py-2 text-sm text-plat-secondary hover:bg-plat-hover"
          >
            <CaretLeft className="h-3.5 w-3.5" /> {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 6 ? (
            <button
              disabled={!canNext}
              onClick={goNext}
              className="inline-flex items-center gap-1 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
            >
              Next <CaretRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
            >
              {createMutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" weight="fill" />}
              Create user
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
